// Data seam for the "Mi cuenta" area. The personal record — the
// person, not the workspace: name, email, language, timezone — plus the active
// sessions where this account is signed in. Stable, important data, so the
// profile follows the view -> edit -> save model (ViewEditCard), not auto-save.
//
// Profile is wired to GET /account/me + sessions to GET /account/sessions
// (seam-wiring A-L2 + A-L3). The writes are wired too:
//   PATCH  /account                 -> saveAccountProfile (name edits via ViewEditCard)
//   DELETE /account/sessions/:id    -> revokeSession (close one session)
//   POST   /account/sessions/revoke-others -> revokeOtherSessions (close every other)
// The photo (Cambiar/Quitar foto) ships DB-backed (Postgres bytea, PR 19):
//   POST   /account/avatar          -> uploadAvatar (multipart)
//   DELETE /account/avatar          -> removeAvatar

import { apiClient } from '../../api/client.js'

export interface AccountProfile {
  name: string
  email: string
  /** Notification-only secondary address; null when not set. Self-service —
   *  edited from this same "Datos personales" card, like the display name. */
  secondaryEmail: string | null
  /** 'pending' until the verify link is clicked, 'verified' after; null when
   *  secondaryEmail is null. Automated mail only routes here once 'verified'. */
  secondaryEmailStatus: 'pending' | 'verified' | null
  language: string
  timezone: string
  /** Human-readable role, e.g. "Dueño". */
  roleLabel: string
  /** Workspace the person belongs to, for the photo card subtitle. */
  workspaceName: string
  /** The workspace's accent colour; the app shell tints itself with it. */
  workspaceAccent: string
  initials: string
  hasAvatar: boolean
}

export interface AccountSession {
  id: string
  /** "Windows · Chrome", "iPhone · Safari". */
  device: string
  kind: 'desktop' | 'mobile'
  /** "Ciudad de México, México". */
  location: string
  /** "Activa ahora", "Hace 2 horas", "Hace 3 días". */
  lastActive: string
  /** The session making this request — cannot be closed from here. */
  current: boolean
}

export interface AccountData {
  profile: AccountProfile
  sessions: AccountSession[]
}

/**
 * Loads the whole "Mi cuenta" page: GET /account/me and GET /account/sessions
 * in parallel. For profile-only consumers (the shell) use getAccountProfile().
 */
export async function getAccount(): Promise<AccountData> {
  const [meRes, sessionsRes] = await Promise.all([
    apiClient.get<{ data: AccountProfile }>('/account/me'),
    apiClient.get<{ data: AccountSession[] }>('/account/sessions'),
  ])
  return { profile: meRes.data.data, sessions: sessionsRes.data.data }
}

/**
 * Just the profile, without the sessions call.
 *
 * The shell needs `workspaceName` and `workspaceAccent` on every page, and it
 * has no business fetching a person's active sessions to draw a sidebar.
 */
export async function getAccountProfile(): Promise<AccountProfile> {
  const res = await apiClient.get<{ data: AccountProfile }>('/account/me')
  return res.data.data
}

export interface AccountProfilePatch {
  name?: string | undefined
  /** Empty string clears it back to unset. */
  secondary_email?: string | undefined
}

/** Save the editable profile fields (display name, secondary email). Only the
 *  keys present are sent — ViewEditCard already diffs before calling this. */
export async function saveAccountProfile(patch: AccountProfilePatch): Promise<void> {
  await apiClient.patch('/account/me', patch)
}

/** Close one remote session by id. */
export async function revokeSession(id: string): Promise<void> {
  await apiClient.delete(`/account/sessions/${encodeURIComponent(id)}`)
}

/** Close every session except the current one. */
export async function revokeOtherSessions(): Promise<void> {
  await apiClient.post('/account/sessions/revoke-others', {})
}

/**
 * Start an email change: re-auths with the current password and, on success,
 * sends a confirmation link to the new address. The account's email does not
 * change until that link is clicked (POST /auth/email/confirm).
 */
export async function requestEmailChange(input: {
  new_email: string
  current_password: string
}): Promise<void> {
  await apiClient.post('/account/email', input)
}

/** Upload/replace the signed-in user's profile photo (max 2 MB, jpeg/png/webp). */
export async function uploadAvatar(file: File): Promise<void> {
  const form = new FormData()
  form.append('file', file)
  await apiClient.post('/account/avatar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/** Remove the signed-in user's profile photo. */
export async function removeAvatar(): Promise<void> {
  await apiClient.delete('/account/avatar')
}
